// Aide au design de l'application générée (lot D1 v0.5.0 — B-501, ART-0172) : client de
// `/api/v1/design`.
//
// Trois objets versionnés par espace — la CHARTE, le PARCOURS d'écrans et ses MAQUETTES — plus
// la CONFORMITÉ d'interface d'une révision en marche. Le document HTML d'une maquette n'est
// JAMAIS lu par l'interface : `getDesignWireframeAccess` rend une adresse courte que le cadre
// `sandbox=""` charge lui-même (ART-0172 règle 1, DES-AC-05).
import { request } from './http'
import type {
  DesignCharterDetail, DesignCharterInput, DesignCharterListResponse, DesignCharterOverride,
  DesignChoices, DesignComparisonDetail, DesignComparisonSummary, DesignConformance, DesignFlowDetail,
  DesignFlowInput, DesignFlowListResponse, DesignFlowProposeInput, DesignFlowProposeResult,
  DesignInheritance, DesignOverrideInput, DesignWireframeAccess, DesignWireframeSummary,
  OrgCharterEnforcement, OrgDesignCharterDetail, OrgDesignCharterListResponse, UsabilityCheck,
  UsabilityCheckInput, UsabilityCheckListResponse, UsabilityCompleteInput,
} from './types/design'

function query(params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams()
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') q.set(cle, String(valeur))
  }
  return q.toString()
}

/** Segment d'URL : tout identifiant est encodé, même numérique (aucune concaténation brute). */
const seg = (valeur: string | number): string => encodeURIComponent(String(valeur))

/** Corps des validations : l'espace, rien d'autre (l'auteur est lu du jeton par la passerelle). */
const corpsEspace = (workspaceId: string): string => JSON.stringify({ workspace_id: workspaceId })

export const designApi = {
  /** Versions de la charte, version validée, catalogue des choix guidés (et neutres fixes). */
  listDesignCharters: (workspaceId: string) =>
    request<DesignCharterListResponse>(`/design/charters?${query({ workspace_id: workspaceId })}`),
  /** Nouvelle version BROUILLON ; 422 `DESIGN_CONTRAST_INSUFFICIENT` si un contraste échoue. */
  createDesignCharter: (input: DesignCharterInput) =>
    request<DesignCharterDetail>('/design/charters', { method: 'POST', body: JSON.stringify(input) }),
  /** Détail d'une version : jetons DTCG et DESIGN.md. */
  getDesignCharter: (workspaceId: string, id: number) =>
    request<DesignCharterDetail>(`/design/charters/${seg(id)}?${query({ workspace_id: workspaceId })}`),
  /** Valide un brouillon (l'auteur ne valide jamais sa propre version : 403 sinon). */
  validateDesignCharter: (workspaceId: string, id: number) =>
    request<DesignCharterDetail>(`/design/charters/${seg(id)}/validate`, {
      method: 'POST', body: corpsEspace(workspaceId),
    }),
  /** Versions du parcours, version validée, propositions de la caste en cours ou échouées. */
  listDesignFlows: (workspaceId: string) =>
    request<DesignFlowListResponse>(`/design/flows?${query({ workspace_id: workspaceId })}`),
  /** Demande à la caste designer un parcours depuis une spécification APPROUVÉE (job). */
  proposeDesignFlow: (input: DesignFlowProposeInput) =>
    request<DesignFlowProposeResult>('/design/flows/propose', { method: 'POST', body: JSON.stringify(input) }),
  /** Enregistre un parcours saisi (brouillon). */
  createDesignFlow: (input: DesignFlowInput) =>
    request<DesignFlowDetail>('/design/flows', { method: 'POST', body: JSON.stringify(input) }),
  /** Détail d'un parcours : écrans, transitions, traçabilité des exigences, maquettes. */
  getDesignFlow: (workspaceId: string, id: number) =>
    request<DesignFlowDetail>(`/design/flows/${seg(id)}?${query({ workspace_id: workspaceId })}`),
  validateDesignFlow: (workspaceId: string, id: number) =>
    request<DesignFlowDetail>(`/design/flows/${seg(id)}/validate`, {
      method: 'POST', body: corpsEspace(workspaceId),
    }),
  /** Adresse courte (jeton) du rendu d'une maquette, pour un cadre `sandbox=""` — jamais le HTML. */
  getDesignWireframeAccess: (workspaceId: string, id: number) =>
    request<DesignWireframeAccess>(`/design/wireframes/${seg(id)}/access?${query({ workspace_id: workspaceId })}`),
  validateDesignWireframe: (workspaceId: string, id: number) =>
    request<DesignWireframeSummary>(`/design/wireframes/${seg(id)}/validate`, {
      method: 'POST', body: corpsEspace(workspaceId),
    }),
  /** Conformité d'interface d'une révision en marche (relevé d'aperçu, DES-07). */
  getDesignConformance: (workspaceId: string, revision: string) =>
    request<DesignConformance>(`/design/revisions/${seg(revision)}/conformance?${query({ workspace_id: workspaceId })}`),

  // ------------------------------------------------------------------ lot D 0.5.x (B-502)

  /** Charte de l'ORGANISATION : versions, version validée, catalogue, droits, surcharges (admin). */
  listOrgDesignCharters: () => request<OrgDesignCharterListResponse>('/org-design-charters'),
  getOrgDesignCharter: (id: number) => request<OrgDesignCharterDetail>(`/org-design-charters/${seg(id)}`),
  /** Nouvelle version BROUILLON (administration) ; 422 `DESIGN_CONTRAST_INSUFFICIENT` sinon. */
  createOrgDesignCharter: (choices: DesignChoices) =>
    request<OrgDesignCharterDetail>('/org-design-charters', { method: 'POST', body: JSON.stringify({ choices }) }),
  /** Valide un brouillon, recommandé (`allowed`) ou imposé (`in_force`) ; auteur ≠ validateur. */
  validateOrgDesignCharter: (id: number, enforcement: OrgCharterEnforcement) =>
    request<OrgDesignCharterDetail>(`/org-design-charters/${seg(id)}/validate`, {
      method: 'POST', body: JSON.stringify({ enforcement }),
    }),
  setOrgDesignCharterEnforcement: (id: number, enforcement: OrgCharterEnforcement) =>
    request<OrgDesignCharterDetail>(`/org-design-charters/${seg(id)}/enforcement`, {
      method: 'POST', body: JSON.stringify({ enforcement }),
    }),
  /** Héritage de la charte par le projet : charte effective, surcharge ouverte, historique. */
  getDesignInheritance: (workspaceId: string) =>
    request<DesignInheritance>(`/design/inheritance?${query({ workspace_id: workspaceId })}`),
  requestDesignOverride: (input: DesignOverrideInput) =>
    request<DesignCharterOverride>('/design/inheritance/overrides', { method: 'POST', body: JSON.stringify(input) }),
  /** Décision d'une dérogation (charte imposée) par l'administration de l'organisation. */
  decideDesignOverride: (workspaceId: string, id: number, decision: 'grant' | 'refuse', comment?: string) =>
    request<DesignCharterOverride>(`/design/inheritance/overrides/${seg(id)}/decide`, {
      method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, decision, comment: comment ?? null }),
    }),
  endDesignOverride: (workspaceId: string, id: number) =>
    request<DesignCharterOverride>(`/design/inheritance/overrides/${seg(id)}/end`, {
      method: 'POST', body: corpsEspace(workspaceId),
    }),
  /** Tests d'usage du projet (DES-09). */
  listUsabilityChecks: (workspaceId: string) =>
    request<UsabilityCheckListResponse>(`/design/usability-checks?${query({ workspace_id: workspaceId })}`),
  getUsabilityCheck: (workspaceId: string, id: number) =>
    request<UsabilityCheck>(`/design/usability-checks/${seg(id)}?${query({ workspace_id: workspaceId })}`),
  createUsabilityCheck: (input: UsabilityCheckInput) =>
    request<UsabilityCheck>('/design/usability-checks', { method: 'POST', body: JSON.stringify(input) }),
  completeUsabilityCheck: (id: number, input: UsabilityCompleteInput) =>
    request<UsabilityCheck>(`/design/usability-checks/${seg(id)}/complete`, {
      method: 'POST', body: JSON.stringify(input),
    }),
  waiveUsabilityCheck: (workspaceId: string, id: number, reason: string) =>
    request<UsabilityCheck>(`/design/usability-checks/${seg(id)}/waive`, {
      method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, reason }),
    }),
  /** Comparaisons maquette ↔ application versées au dossier (DES-08). */
  listDesignComparisons: (workspaceId: string, revision?: string) =>
    request<{ items: DesignComparisonSummary[] }>(`/design/comparisons?${query({ workspace_id: workspaceId, revision })}`),
  getDesignComparison: (workspaceId: string, id: number) =>
    request<DesignComparisonDetail>(`/design/comparisons/${seg(id)}?${query({ workspace_id: workspaceId })}`),
  createDesignComparison: (workspaceId: string, revision: string) =>
    request<DesignComparisonDetail>('/design/comparisons', {
      method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, revision }),
    }),
}
